import { useEffect, useState } from "react";
import styled from "styled-components";
import {
  CaptionIcon,
  CaptionRow,
  CaptionText,
  ConsentItems,
} from "./ConsentItems";
import HashSigningImage from "./HashSigningImage";
import HashSigningInstructions, {
  HashSigningDeeplinkButton,
  HashSigningInstructionsProps,
  HashSigningInstructionsText,
} from "./HashSigningInstructions";
import { UseCase } from "../../base/UseCase";
import dotOpen from "../../../images/dot-open.svg";
import dotClosed from "../../../images/dot-closed.svg";

const InformingBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const ContentContainer = styled.div<{ $isDesktop: boolean }>`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;
`;

const HashSigningTextBlock = styled.div`
  order: 1;
`;

const HashSigningImageBlock = styled.div`
  order: 2;
`;

const HashSigningButtonBlock = styled.div`
  order: 3;
`;

const PinBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const PinDots = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px;
  gap: 8px;
  width: 248px;
  height: 56px;
  background: #163563;
  border-radius: 8px;
  box-sizing: border-box;
`;

const PinDotsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const DotWrapper = styled.span`
  width: 24px;
  height: 24px;
  display: inline-flex;
  position: relative;
`;

const DotBase = styled.img`
  width: 24px;
  height: 24px;
  display: block;
`;

const NonPKIoAlert = styled.p`
  color: red;
  text-align: center;
`;

export interface InformingProps {
  attributes: Array<string> | undefined;
  isDesktop: boolean;
  showNonPKIoAlert: boolean;
  t: InformingConsentText;
  logoUri?: string | undefined;
  hashSigningInstructions?: HashSigningInstructionsProps | undefined;
  useCase: UseCase;
}

export interface InformingConsentText {
  enterPinInstructions: string;
  confirmationText: string;
  disclaimerText: string;
  clientContextHeaderText: string;
  hashSigningInstructions?: HashSigningInstructionsText | undefined;
  nonPKIoWarning: string;
  onlyOpenIdScopeText: string;
}

export default function Informing({
  attributes,
  isDesktop,
  showNonPKIoAlert,
  useCase,
  hashSigningInstructions,
  t,
}: InformingProps): JSX.Element {
  const isHashSigning = useCase === "HashSigning";
  const [pinStep, setPinStep] = useState(0);

  useEffect(() => {
    let current = 0;
    const intervalId = setInterval(() => {
      current = (current + 1) % 6;
      setPinStep(current);
    }, 500);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <InformingBox data-testid="consent_informing">
      {useCase === "Identification" && (
        <PinBlock>
          <PinDots data-testid="consent_pin_animation">
            <PinDotsRow>
              <DotWrapper>
                <DotBase src={pinStep >= 1 ? dotClosed : dotOpen} alt="" />
              </DotWrapper>
              <DotWrapper>
                <DotBase src={pinStep >= 2 ? dotClosed : dotOpen} alt="" />
              </DotWrapper>
              <DotWrapper>
                <DotBase src={pinStep >= 3 ? dotClosed : dotOpen} alt="" />
              </DotWrapper>
              <DotWrapper>
                <DotBase src={pinStep >= 4 ? dotClosed : dotOpen} alt="" />
              </DotWrapper>
              <DotWrapper>
                <DotBase src={pinStep >= 5 ? dotClosed : dotOpen} alt="" />
              </DotWrapper>
            </PinDotsRow>
          </PinDots>
          <CaptionRow data-testid="consent_confirmation_text">
            <CaptionIcon aria-hidden="true">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="24" height="24" rx="8" fill="#371F13" />
                <path
                  d="M16.9007 14.7281C16.911 14.8303 16.9161 14.9606 16.9161 15.119C16.9161 15.2722 16.9059 15.4511 16.8854 15.6554C16.8701 15.8547 16.8497 16.0488 16.8241 16.2378C16.7577 16.667 16.6913 16.9735 16.6249 17.1575C15.5315 17.7041 14.1112 17.9774 12.364 17.9774C10.7291 17.9774 9.33437 17.5457 8.17974 16.6823C7.33676 16.0539 6.74667 15.1726 6.40948 14.0384C6.23578 13.456 6.14893 12.7484 6.14893 11.9157C6.14893 11.0778 6.30986 10.2884 6.63172 9.54765C6.95359 8.80174 7.40318 8.16823 7.98049 7.64711C9.1811 6.56401 10.7393 6.02246 12.6552 6.02246C13.0128 6.02246 13.4803 6.05567 14.0576 6.12209C14.6349 6.1885 15.013 6.22937 15.1918 6.2447L15.7129 6.29835C16.1063 6.33411 16.3643 6.35199 16.4869 6.35199H16.7322C16.7679 6.35199 16.8241 6.34688 16.9007 6.33666C16.911 6.45928 16.9161 6.74793 16.9161 7.20263C16.9161 7.65733 16.8343 8.42112 16.6708 9.494H15.6899C15.5929 8.72255 15.4702 8.18866 15.3221 7.89234C14.898 7.21285 13.9708 6.8731 12.5402 6.8731C11.1097 6.8731 9.99342 7.34313 9.19132 8.28318C8.42497 9.17725 8.0418 10.36 8.0418 11.8314C8.0418 13.5326 8.52204 14.8635 9.48253 15.824C10.3613 16.7028 11.4342 17.1421 12.7012 17.1421C13.6157 17.1421 14.3105 16.9684 14.7856 16.621C15.2659 16.2685 15.6516 15.6375 15.9428 14.7281H16.9007Z"
                  fill="#FAF5EB"
                />
              </svg>
            </CaptionIcon>
            <CaptionText>{t.confirmationText}</CaptionText>
          </CaptionRow>
        </PinBlock>
      )}
      <ContentContainer $isDesktop={isDesktop}>
        {isHashSigning ? (
          <>
            {hashSigningInstructions && (
              <HashSigningTextBlock>
                <HashSigningInstructions {...hashSigningInstructions} />
              </HashSigningTextBlock>
            )}
            <HashSigningImageBlock>
              <HashSigningImage />
            </HashSigningImageBlock>
            {hashSigningInstructions && !isDesktop && (
              <HashSigningButtonBlock>
                <HashSigningDeeplinkButton
                  label={hashSigningInstructions.t?.openCleverbase ?? ""}
                  transactionId={hashSigningInstructions.transactionId}
                  isDesktop={isDesktop}
                />
              </HashSigningButtonBlock>
            )}
          </>
        ) : (
          <ConsentItems
            confirmationText={t.confirmationText}
            disclaimerText={t.disclaimerText}
            toShareAttributes={attributes}
            isDesktop={isDesktop}
            onlyOpenIdScopeText={t.onlyOpenIdScopeText}
            showConfirmationText={false}
          />
        )}
        {showNonPKIoAlert && <NonPKIoAlert>{t.nonPKIoWarning}</NonPKIoAlert>}
      </ContentContainer>
    </InformingBox>
  );
}
